<template>
  <!-- Fullscreen wrapper with background pattern -->
  <section class="screen" aria-label="Certificate options screen" ref="screen">
    <!-- STAGE reserves the scaled height so there’s no overflow -->
    <div class="fit-stage" :style="{ height: scaledHeight + 'px' }">
      <!-- CANVAS is the thing we uniformly scale -->
      <div
        class="fit-canvas"
        ref="fitCanvas"
        :style="{ transform: `scale(${scale})` }"
      >
        <div class="logo-wrapper">
          <img
            src="../assets/img/Diamond-Services-ID.png"
            alt="Diamond Services Logo"
            class="logo"
          />
        </div>

        <div v-if="showCard" class="print-button">
          <button class="search" @click="showCard = false">Go Back</button>
        </div>

        <div v-if="!showCard" class="content">
          <!-- Option 1: Digital (video or iframe) -->
          <component
            :is="linkTag"
            :to="isRouter ? digitalVideoSrc : undefined"
            :href="!isRouter ? digitalVideoSrc : undefined"
            class="option-card"
            data-kind="digital"
            :aria-label="`Open ${digitalTitle}`"
          >
            <div class="media">
              <!-- VIDEO MODE -->
              <video
                v-if="!digitalAsIframe && digitalVideoSrc"
                ref="digitalVideo"
                class="media-el"
                :poster="digitalPoster || ''"
                muted
                loop
                playsinline
                autoplay
                preload="auto"
                aria-hidden="true"
              >
                <source :src="digitalVideoSrc" type="video/mp4" />
              </video>

              <!-- IFRAME MODE -->
              <iframe
                v-else-if="digitalAsIframe && digitalIframeSrc"
                class="media-el iframe-bg"
                :src="digitalIframeSrc"
                title="Digital certificate preview"
                allow="autoplay; encrypted-media"
                allowfullscreen
                aria-hidden="true"
              ></iframe>

              <!-- Poster fallback -->
              <div
                v-else
                class="media-el media-poster"
                :style="digitalPosterStyle"
                aria-hidden="true"
              />
            </div>

            <div class="overlay glass">
              <h2 class="title">{{ digitalTitle }}</h2>
              <button
                class="btn-view"
                type="button"
                @click.prevent="open(digitalVideoSrc)"
              >
                View
              </button>
            </div>
          </component>

          <!-- Option 2: Card -->
          <div
            class="option-card"
            data-kind="card"
            :aria-label="`Open ${cardTitle}`"
          >
            <CardCert
              :certificate="certificate"
              :certImage="certImage"
              :isHelzberg="isHelzberg"
            />
          </div>
        </div>

        <div v-else class="content">
          <CardCert
            :certificate="certificate"
            :certImage="certImage"
            :isHelzberg="isHelzberg"
          />
        </div>
      </div>
      <!-- /fit-canvas -->
    </div>
    <!-- /fit-stage -->
  </section>
</template>

<script>
import { db } from "@/config/firebaseInit";
import CardCert from "../components/CardCert.vue";
import { resolvePngOrJpg } from "@/services/dropbox";

export default {
  name: "CertificateOptions",
  components: { CardCert },
  props: {
    cardLink: {
      type: String,
      default: "https://certificates.diamondservicesusa.com/jewelry/573641",
    },
    digitalPoster: {
      type: String,
      default:
        "https://firebasestorage.googleapis.com/v0/b/diamond-usa-staging.appspot.com/o/CertThumbnail.png?alt=media&token=50b5b995-f264-4b6f-9855-c9b0de8a2888",
    },
    digitalAsIframe: { type: Boolean, default: true },
    digitalIframeUrl: {
      type: String,
      default: "https://certificates.diamondservicesusa.com/jewelry/573641",
    },
    digitalTitle: { type: String, default: "Digital Certificate" },
    cardTitle: { type: String, default: "Card Certificate" },
    useRouterLink: { type: Boolean, default: true },
    appBg: { type: String, default: "../assets/img/BACKGROUD DIS.png" },
    primaryColor: { type: String, default: "#2F6DF6" },
    primaryColorHover: { type: String, default: "#2557C4" },
  },
  data() {
    return {
      io: null,
      showCard: false,
      certImage: null,
      certificate: {},
      fileNameOptions: { CertNum: false, ClientSKU: false },
      // fit-to-viewport state
      scale: 1,
      scaledHeight: 0,
      ro: null,
    };
  },
  computed: {
    isHelzberg() {
      if (!this.certificate.Company) return false;
      return this.certificate.Company.name.includes("Helzberg");
    },
    dropboxCertPath() {
      return this.isHelzberg
        ? "/Helzberg/IMAGES/"
        : "/Card-Certificate Images/";
    },
    certificateID() {
      return this.$route.params.id;
    },
    digitalVideoSrc() {
      return `https://certificates.diamondservicesusa.com/jewelry/${this.certificateID}`;
    },
    isRouter() {
      return this.useRouterLink && !!this.$router;
    },
    linkTag() {
      return this.isRouter ? "router-link" : "a";
    },
    digitalPosterStyle() {
      return this.digitalPoster
        ? { backgroundImage: `url(${this.digitalPoster})` }
        : {};
    },
    cardBgStyle() {
      return {
        backgroundImage: `url(${this.cardBackground})`,
        backgroundPosition: "center",
        backgroundSize: "98%",
        backgroundRepeat: "no-repeat",
      };
    },
    digitalIframeSrc() {
      const url = new URL(
        `https://certificates.diamondservicesusa.com/jewelry/${this.certificateID}`,
        window.location.origin
      );
      if (!url.searchParams.has("autoplay"))
        url.searchParams.set("autoplay", "1");
      if (!url.searchParams.has("muted")) url.searchParams.set("muted", "1");
      if (!url.searchParams.has("loop")) url.searchParams.set("loop", "1");
      if (!url.searchParams.has("controls"))
        url.searchParams.set("controls", "0");
      return url.toString();
    },
  },
  watch: {
    // when toggling showCard, re-measure to keep everything in view
    showCard() {
      this.$nextTick(() => {
        this.measureFit();
      });
    },
  },
  created() {
    this.fetchCertificate(this.certificateID);
  },
  mounted() {
    // Fit-to-viewport scaler
    this.$nextTick(async () => {
      await new Promise((r) => setTimeout(r, 50));
      if (document.fonts && document.fonts.ready) {
        try {
          await document.fonts.ready;
        } catch (err) {
          console.log(err);
        }
      }
      this.measureFit();

      // re-measure on viewport resize/rotation
      this.ro = new ResizeObserver(() => this.measureFit());
      this.ro.observe(this.$refs.screen);

      // images/video/iframe may load later → re-measure
      for (const el of this.$el.querySelectorAll("img, video, iframe")) {
        if (el.tagName === "IMG" && !el.complete)
          el.addEventListener("load", () => this.measureFit(), { once: true });
        if (el.tagName === "IFRAME")
          el.addEventListener("load", () => this.measureFit(), { once: true });
      }
    });

    // Autoplay when visible (video mode)
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const vid = this.$refs.digitalVideo;
    if (vid && !prefersReduced) {
      const tryPlay = () => vid.play().catch(() => {});
      vid.addEventListener("canplay", tryPlay, { once: true });

      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) =>
            e.isIntersecting ? vid.play().catch(() => {}) : vid.pause()
          );
        },
        { threshold: 0.25 }
      );
      this.io.observe(vid);
    }
  },
  beforeDestroy() {
    if (this.io) this.io.disconnect();
    if (this.ro) this.ro.disconnect();
  },
  unmounted() {
    if (this.io) this.io.disconnect();
    if (this.ro) this.ro.disconnect();
  },
  methods: {
    measureFit() {
      const screen = this.$refs.screen;
      const canvas = this.$refs.fitCanvas;
      if (!screen || !canvas) return;

      // 1) Temporarily remove transform to read the layout's natural size
      const prev = canvas.style.transform;
      canvas.style.transform = "none";

      const naturalW = canvas.scrollWidth || canvas.offsetWidth || 1;
      const naturalH = canvas.scrollHeight || canvas.offsetHeight || 1;

      // 2) Available viewport inside .screen (minus padding)
      const styles = getComputedStyle(screen);
      const padX =
        parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      const padY =
        parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      const availW = screen.clientWidth - padX;
      const availH = screen.clientHeight - padY;

      // 3) Compute uniform scale so everything fits (no scrolling)
      const s = Math.min(1, availW / naturalW, availH / naturalH);
      this.scale = s;
      this.scaledHeight = Math.ceil(naturalH * s);

      // restore
      canvas.style.transform = prev;
    },

    async resolveDropboxImages(key) {
      const value = this.certificate[key];
      if (!value) return null;
      const base = `${this.dropboxCertPath}${value}`;
      try {
        const link = await resolvePngOrJpg(base);
        this.certImage = link;
        this.$nextTick(() => this.measureFit());
        return link;
      } catch (err) {
        this.fileNameOptions[key] = true;
        if (this.fileNameOptions.CertNum && this.fileNameOptions.ClientSKU)
          return null;
        return this.resolveDropboxImages("CertNum");
      }
    },
    async fetchCertificate(id) {
      let loader = this.$loading.show({ container: null, canCancel: false });
      try {
        const certificate = await db.collection("jewelry").doc(id).get();
        this.certificate = certificate.data();
        await this.resolveDropboxImages("ClientSKU");
      } catch (error) {
        console.log(error);
      } finally {
        loader.hide();
        this.$nextTick(() => this.measureFit());
      }
    },
    open(href) {
      this.isRouter && href.startsWith("/")
        ? this.$router.push(href)
        : (window.location.href = href);
    },
  },
};
</script>

<style scoped>
/* ----- Fullscreen & centering ----- */
.screen {
  height: 100vh;
  padding: calc(env(safe-area-inset-top, 0px) + 16px) 16px
    calc(env(safe-area-inset-bottom, 0px) + 16px);
  background-color: #212e51;
  background-position: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden; /* no scrolling */
}

/* The scaler wrapper */
.fit-stage {
  width: 100%;
  display: flex;
  justify-content: center;
}
.fit-canvas {
  transform-origin: top center;
  will-change: transform;
  -webkit-font-smoothing: antialiased;
  backface-visibility: hidden;
}

/* Layout inside the canvas */
.content {
  width: 100%;
  max-width: 440px;
  display: grid;
  gap: 2rem;
}

/* Logo styles */
.logo-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
}
.logo {
  height: 40px;
  filter: invert(1) brightness(2);
  object-fit: contain;
}

/* Cards */
.option-card {
  position: relative;
  display: block;
  border-radius: 18px;
  overflow: hidden;
  min-height: 300px;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.28);
  transform: translateZ(0);
}

.media {
  position: absolute;
  inset: 0;
  background: #0f121a;
}
.media-el {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  border: 0;
}
.iframe-bg {
  pointer-events: none;
}
.media-poster {
  background-position: center;
  background-size: cover;
}

/* Overlay */
.glass {
  position: relative;
  z-index: 2;
  height: 100%;
  display: grid;
  align-content: end;
  gap: 12px;
  padding: 20px 16px;
  background: linear-gradient(
    180deg,
    rgba(10, 11, 16, 0.15) 0%,
    rgba(10, 11, 16, 0.55) 100%
  );
  -webkit-backdrop-filter: blur(1px);
  backdrop-filter: blur(1px);
}
.title {
  color: #fff;
  font-size: 1.28rem;
  line-height: 1.2;
  font-weight: 800;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
}
.btn-view {
  appearance: none;
  border: 0;
  border-radius: 999px;
  padding: 12px 18px;
  font-weight: 800;
  font-size: 0.98rem;
  background: v-bind("primaryColor");
  color: #7c9cc8;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  cursor: pointer;
}
.btn-view:hover {
  background: v-bind("primaryColorHover");
}
.btn-view:active {
  transform: translateY(1px);
}

/* Buttons row when showCard is true */
.print-button {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
}
.search {
  width: min(440px, 92%);
  padding: 12px 16px;
  border: 0;
  border-radius: 999px;
  font-weight: 800;
  font-size: clamp(14px, 4vw, 18px);
  background: #2f6df6;
  color: #fff;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.25);
}

/* (Optional) Slightly taller cards on larger screens; the scaler will adapt anyway */
@media (min-width: 480px) {
  .option-card {
    min-height: 320px;
  }
  .title {
    font-size: 1.35rem;
  }
}
@media (min-width: 768px) {
  .content {
    gap: 22px;
  }
  .option-card {
    min-height: 360px;
  }
}
</style>
